import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Users, AlertTriangle, ArrowRight, Gamepad2 } from 'lucide-react'
import { teamsService } from '../../services/teamsService'
import type { Team } from '../../types/teams'
import SmoothLoadingSpinner from '../common/SmoothLoadingSpinner'
import { analyticsService } from '../../services/analyticsService'
import { FILL_GRADIENT } from '../../constants/gradients'
import { TeamTag } from '../common/TeamTag'

interface ProjectTransferModalProps {
  isOpen: boolean
  onClose: () => void
  projectId: string
  projectName: string
  projectIconUrl?: string
  currentTeamId?: string
  currentTeam?: { id: string; name: string; avatar_url?: string; is_personal?: boolean } | null
  onTransfer: (teamId: string) => Promise<void>
}

export const ProjectTransferModal = ({
  isOpen,
  onClose,
  projectId,
  projectName,
  projectIconUrl,
  currentTeamId,
  currentTeam,
  onTransfer
}: ProjectTransferModalProps) => {
  const [teams, setTeams] = useState<Team[]>([])
  const [selectedTeamId, setSelectedTeamId] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingTeams, setIsLoadingTeams] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Fetch available teams when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchTeams()
      setSelectedTeamId('')
      setError(null)
      
      // Track modal opened
      analyticsService.track({
        name: 'modal_opened',
        properties: {
          modal_type: 'project_transfer',
          project_id: projectId,
          current_team_id: currentTeamId
        }
      });
    }
  }, [isOpen])

  const fetchTeams = async () => {
    try {
      setIsLoadingTeams(true)
      setError(null)
      
      const allTeams = await teamsService.getUserTeams(true)
      
      // Find the current team to check if it's personal
      const currentTeam = allTeams.find(team => team.id === currentTeamId)
      const isCurrentTeamPersonal = currentTeam?.type === 'personal'
      
      // Filter logic:
      // 1. Exclude the current team
      // 2. If current team is personal, exclude ALL personal teams
      const availableTeams = allTeams.filter(team => {
        if (team.id === currentTeamId) return false
        if (isCurrentTeamPersonal && team.type === 'personal') return false
        return true
      })
      
      setTeams(availableTeams)
    } catch (error) {
      console.error('Failed to fetch teams:', error)
      setError('Failed to load teams. Please try again.')
    } finally {
      setIsLoadingTeams(false)
    }
  }

  const handleTransfer = async () => {
    if (!selectedTeamId) {
      setError('Please select a team to transfer to.')
      return
    }

    try {
      setIsLoading(true)
      setError(null)
      
      await onTransfer(selectedTeamId)
      
      // Close modal on success
      onClose()
    } catch (error) {
      console.error('Transfer failed:', error)
      setError(error instanceof Error ? error.message : 'Transfer failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const selectedTeam = teams.find(team => team.id === selectedTeamId)

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Transfer Game
              </h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6 max-h-[calc(90vh-140px)] overflow-y-auto">
              {/* Project Info */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-600 flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-600 dark:to-gray-700 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {projectIconUrl ? (
                    <img
                      src={projectIconUrl}
                      alt={projectName}
                      className="w-full h-full object-contain"
                      onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
                    />
                  ) : (
                    <Gamepad2 className="w-7 h-7 text-gray-400" />
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white text-lg mb-1.5">
                    {projectName}
                  </h3>
                  {currentTeam && !currentTeam.is_personal && (
                    <TeamTag team={currentTeam} size="sm" />
                  )}
                </div>
              </div>

              {/* Warning */}
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-start space-x-3 p-4 bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-xl border border-yellow-300 dark:border-yellow-700"
              >
                <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="text-yellow-900 dark:text-yellow-200 font-semibold mb-1">
                    Important Notice
                  </p>
                  <p className="text-yellow-800 dark:text-yellow-300">
                    This will move all versions, data, and access rights to the selected team. This action cannot be undone.
                  </p>
                </div>
              </motion.div>

              {/* Team Selection */}
              <div className="space-y-3">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Select destination team
                </label>
                
                {isLoadingTeams ? (
                  <div className="flex items-center justify-center py-8">
                    <SmoothLoadingSpinner isLoading={true} size="md" />
                  </div>
                ) : teams.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 dark:text-gray-400 font-medium">
                      No teams available for transfer.
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                      You need to be a member of other teams to transfer this project.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto px-1 py-1">
                    {teams.map((team) => (
                      <motion.label
                        key={team.id}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                        className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          selectedTeamId === team.id
                            ? 'border-primary-500 bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/30 dark:to-primary-800/30 shadow-md'
                            : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="team"
                          value={team.id}
                          checked={selectedTeamId === team.id}
                          onChange={(e) => setSelectedTeamId(e.target.value)}
                          className="text-primary-600 focus:ring-primary-500 border-gray-300 w-5 h-5"
                        />
                        <div className="ml-4 flex-1">
                          <div className="flex items-center space-x-2">
                            <Users className={`w-4 h-4 ${selectedTeamId === team.id ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400'}`} />
                            <span className={`font-semibold ${selectedTeamId === team.id ? 'text-primary-900 dark:text-primary-100' : 'text-gray-900 dark:text-white'}`}>
                              {team.type === 'personal' ? `${team.name} (Personal)` : team.name}
                            </span>
                          </div>
                          {team.description && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 ml-6">
                              {team.description}
                            </p>
                          )}
                        </div>
                      </motion.label>
                    ))}
                  </div>
                )}
              </div>

              {/* Error Message */}
              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="p-4 bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/30 dark:to-red-800/30 border-2 border-red-300 dark:border-red-700 rounded-xl"
                  >
                    <p className="text-sm font-medium text-red-700 dark:text-red-300">{error}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
              <button
                onClick={onClose}
                className="px-6 py-2.5 rounded-xl font-semibold text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleTransfer}
                disabled={!selectedTeamId || isLoading || teams.length === 0}
                style={{
                  background: !selectedTeamId || isLoading || teams.length === 0 ? undefined : FILL_GRADIENT,
                }}
                className={`px-6 py-2.5 rounded-xl font-semibold text-white flex items-center space-x-2 transition-all ${
                  !selectedTeamId || isLoading || teams.length === 0
                    ? 'bg-gray-300 dark:bg-gray-700 cursor-not-allowed opacity-50'
                    : 'hover:shadow-lg hover:scale-105'
                }`}
              >
                {isLoading ? (
                  <>
                    <SmoothLoadingSpinner isLoading={true} size="sm" />
                    <span>Transferring...</span>
                  </>
                ) : (
                  <>
                    <span>Transfer to {selectedTeam?.name || 'Team'}</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
} 